import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { RecordSelectionCheckbox } from "@/components/records/RecordSelectionCheckbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantConfig } from "@/hooks/use-tenant-config";
import { ApiError } from "@/lib/api/client";
import { refreshHomeAfterAct } from "@/lib/home-query-keys";
import {
  sessionRecordingsApi,
  sessionRecordingsQueryKey,
  type RecordingHistoryEvent,
  type RecordingTransitionAction,
  type TeachingSessionRecordings,
} from "@/lib/api/session-recordings";

/**
 * The recording on a session whose tenant shares recordings by link, as one
 * line under Next steps (ADR 0028 as amended, board 1 decision 9). The line
 * says what the recording is and whether students can open it; the teacher
 * enters a share link, reviews it in the Publish recording sheet, and
 * publishes it once the provider's sign-in rule is attested. No projection
 * carries the URL: the teacher reads it from `review`. A corrected link is
 * withdrawn, then added.
 */

/** The four facts a destination review asserts (ADR 0028), here held by IMAM's checks and the teacher's attestation. */
const REVIEW_CONFIRMATION = {
  authenticated: true,
  no_chat: true,
  no_credentials: true,
  no_admin_controls: true,
} as const;

const EVENT_WORD: Record<RecordingTransitionAction, string> = {
  publish: "Published",
  withhold: "Withheld",
  correct: "Renamed",
  withdraw: "Withdrawn",
  supersede: "Replaced",
};

const quietAct = "min-w-[var(--control-height-compact)] px-0";

function when(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function RecordingLinkLine({
  sessionId,
  identityKey,
  projection,
  classOrdinal,
}: {
  sessionId: string;
  identityKey: string;
  projection: TeachingSessionRecordings;
  classOrdinal?: number | null;
}) {
  const queryClient = useQueryClient();
  const { me } = useAuth();
  const { recordingPolicy, name: tenantName } = useTenantConfig();
  const queryKey = sessionRecordingsQueryKey(sessionId, identityKey);
  const link = projection.link;
  const status = link?.publication_status ?? null;
  const live = status === "withheld" || status === "published";
  const sessionWord = classOrdinal != null ? `Session ${classOrdinal}` : "this session";
  const defaultName = classOrdinal != null ? `Session ${classOrdinal} recording` : "Session recording";
  const school = tenantName ?? "the school";
  const gateSentence = `It can publish once ${school}'s Zoom account requires sign-in on recordings. Students can't open it until then.`;

  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [attested, setAttested] = useState(false);
  const [label, setLabel] = useState(defaultName);
  const [renaming, setRenaming] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [history, setHistory] = useState<RecordingHistoryEvent[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [receipt, setReceipt] = useState<string | null>(null);
  const [readbackBlocked, setReadbackBlocked] = useState(false);
  const field = useRef<HTMLInputElement>(null);
  const reviewOpener = useRef<HTMLButtonElement>(null);
  const withdrawOpener = useRef<HTMLButtonElement>(null);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  // The stored URL is read from `review` (no provider I/O): the sheet needs it
  // to be reviewed, and a published line opens it.
  const reviewWanted = link !== null && ((status === "published") || (sheetOpen && live));
  const review = useQuery({
    queryKey: ["session-recording-review", identityKey, sessionId, link?.link_id ?? null],
    queryFn: () => sessionRecordingsApi.review(sessionId, link!.link_id),
    enabled: reviewWanted,
    retry: false,
    staleTime: 0,
  });

  async function refreshProjection() {
    const result = await sessionRecordingsApi.get(sessionId);
    if (!active.current) return result;
    queryClient.setQueryData(queryKey, result);
    refreshHomeAfterAct(queryClient);
    setReadbackBlocked(false);
    return result;
  }

  async function saveLink() {
    const typed = url.trim();
    if (!typed) {
      setFieldError("Paste the share link from Zoom.");
      field.current?.focus();
      return;
    }
    setBusy("add");
    setFieldError(null);
    try {
      const result = await sessionRecordingsApi.addLink(sessionId, typed);
      if (!active.current) return;
      if (result.projection) {
        queryClient.setQueryData(queryKey, result.projection);
        refreshHomeAfterAct(queryClient);
      } else {
        await refreshProjection();
      }
      setEditing(false);
      setUrl("");
      setReceipt(null);
      setHistory(null);
    } catch (error) {
      if (!active.current) return;
      if (error instanceof ApiError && (error.status === 422 || error.status === 409)) {
        setFieldError(error.message);
        if (error.status === 409) {
          // Another link is already on the session: show what is saved.
          try { await refreshProjection(); } catch { /* the words above stand */ }
        }
      } else {
        setFieldError("The link could not be saved. Your link is kept here; try again.");
      }
      requestAnimationFrame(() => field.current?.focus());
    } finally {
      if (active.current) setBusy(null);
    }
  }

  async function recordNotice() {
    if (!recordingPolicy) return;
    setBusy("notice");
    setSheetError(null);
    try {
      await sessionRecordingsApi.recordNotice(sessionId, recordingPolicy.version, "confirmed");
      await refreshProjection();
    } catch {
      if (!active.current) return;
      setSheetError("It is not clear whether the notice was saved. Check it before publishing.");
      try { await refreshProjection(); } catch { setReadbackBlocked(true); }
    } finally {
      if (active.current) setBusy(null);
    }
  }

  async function transition(action: RecordingTransitionAction, reason?: string) {
    if (!link) return;
    setBusy(action);
    setMessage(null);
    setSheetError(null);
    try {
      const result = await sessionRecordingsApi.transition(sessionId, link.link_id, {
        action,
        revision: link.revision,
        // The name belongs to publishing; a withdrawal never resends it.
        ...(action === "publish" || action === "correct" ? { display_label: label.trim() || defaultName } : {}),
        ...(action === "withdraw" ? { reason } : {}),
        ...(action === "publish" || action === "correct" ? { destination_review: REVIEW_CONFIRMATION } : {}),
      });
      if (!active.current) return;
      if ("projection" in result) {
        queryClient.setQueryData(queryKey, result.projection);
        refreshHomeAfterAct(queryClient);
      } else {
        try {
          await refreshProjection();
        } catch {
          if (!active.current) return;
          setReadbackBlocked(true);
          setMessage("The change was saved, but the recording could not be reloaded. Reload it before changing anything else.");
          return;
        }
      }
      setSheetOpen(false);
      setAttested(false);
      setRenaming(false);
      setWithdrawing(false);
      setWithdrawReason("");
      setHistory(null);
      if (action === "publish") setReceipt(`Students enrolled on this course can open it from ${sessionWord}.`);
      if (action === "withdraw") setReceipt(null);
    } catch (error) {
      if (!active.current) return;
      if (action === "publish" && error instanceof ApiError && error.status === 409) {
        // The gate or a newer revision: the server's sentence, in the sheet, nothing closed.
        setSheetError(error.message);
        try { await refreshProjection(); } catch { setReadbackBlocked(true); }
        return;
      }
      let readbackSucceeded = true;
      try { await refreshProjection(); } catch { readbackSucceeded = false; }
      if (!active.current) return;
      if (!readbackSucceeded) setReadbackBlocked(true);
      const words = readbackSucceeded
        ? "It is not clear whether that was saved, so the recording was reloaded. Check it before trying again."
        : "It is not clear whether that was saved, and the recording could not be reloaded. Reload it before trying again.";
      if (action === "publish") setSheetError(words); else setMessage(words);
    } finally {
      if (active.current) setBusy(null);
    }
  }

  async function showHistory() {
    const placementId = link?.placement_id;
    if (!placementId) return;
    setBusy("history");
    try {
      const result = await sessionRecordingsApi.history(sessionId, placementId);
      if (!active.current) return;
      setHistory(result);
    } catch {
      if (!active.current) return;
      setMessage("The recording's history could not be loaded.");
    } finally {
      if (active.current) setBusy(null);
    }
  }

  function closeSheet() {
    setSheetOpen(false);
    setAttested(false);
    setRenaming(false);
    setSheetError(null);
  }

  function closeWithdraw() {
    setWithdrawing(false);
    setWithdrawReason("");
    withdrawOpener.current?.focus();
  }

  const notice = projection.notice;
  const noticeConfirmed = notice?.status === "confirmed" || notice?.status === "approved_exception";
  const noticeBy = notice && notice.recorded_by === me?.user_id ? " by you" : "";
  const noticeMeta = notice
    ? `${notice.status === "approved_exception" ? "Approved exception recorded" : notice.status === "opted_out" ? "Kept unrecorded" : "Confirmed"}${noticeBy}, ${when(notice.recorded_at)}.`
    : recordingPolicy?.class_notice ?? null;
  const locked = busy !== null || readbackBlocked;
  const canPublish = status === "withheld" && projection.signin_required && noticeConfirmed
    && review.isSuccess && attested && !locked;
  const providerWord = link?.provider_word ?? "Zoom share link";
  const shownLabel = link?.display_label?.trim() || defaultName;
  const audience = projection.audience_sentence;

  const headingId = `recording-link-heading-${sessionId}`;
  // Close's Recording part already says "Recording": the section keeps the name without a second heading.
  return (
    <section aria-labelledby={headingId} className="grid gap-3">
      <span id={headingId} hidden>Recording</span>

      {/* No live link: the line says so; the act is there once the session has ended. */}
      {!live && !editing && (
        <div className="grid justify-items-start gap-1">
          {status === "withdrawn" && link ? (
            <>
              <p className="text-ui text-foreground">
                {link.published_at ? `${shownLabel} · Withdrawn` : `${providerWord} · Removed`}
              </p>
              <p className="text-meta text-muted-foreground">
                Removed from the session{link.withdrawn_at ? ` on ${when(link.withdrawn_at)}` : ""}. The recording stays in Zoom.
              </p>
            </>
          ) : (
            <p className="text-ui text-muted-foreground">
              {projection.link_addable ? "No recording yet." : "The recording can be added once the session has ended."}
            </p>
          )}
          {projection.link_addable && (
            <Button type="button" variant="link" size="sm" className={quietAct} disabled={locked} onClick={() => setEditing(true)}>
              Add recording link
            </Button>
          )}
        </div>
      )}

      {editing && (
        <form
          className="grid max-w-md gap-2"
          noValidate
          onSubmit={(event) => { event.preventDefault(); void saveLink(); }}
        >
          <Label htmlFor={`recording-link-url-${sessionId}`}>Recording link</Label>
          <Input
            ref={field}
            id={`recording-link-url-${sessionId}`}
            type="url"
            inputMode="url"
            autoComplete="off"
            autoFocus
            value={url}
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={`recording-link-help-${sessionId}${fieldError ? ` recording-link-error-${sessionId}` : ""}`}
            onChange={(event) => { setUrl(event.target.value); if (fieldError) setFieldError(null); }}
          />
          {fieldError && (
            <p id={`recording-link-error-${sessionId}`} role="alert" className="text-ui text-destructive">{fieldError}</p>
          )}
          <p id={`recording-link-help-${sessionId}`} className="text-meta text-muted-foreground">
            {projection.signin_required
              ? "It is saved Not published. You review it before students can open it."
              : `It is saved Not published. ${gateSentence}`}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="outline" disabled={busy !== null}>
              {busy === "add" && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
              Save recording link
            </Button>
            <Button type="button" variant="outline" disabled={busy !== null} onClick={() => { setEditing(false); setUrl(""); setFieldError(null); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {status === "withheld" && link && (
        <div className="grid justify-items-start gap-1">
          <p className="text-ui text-foreground">{providerWord} · Not published</p>
          <p className="text-meta text-muted-foreground">
            {projection.signin_required ? "Review it before students can open it." : gateSentence}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {projection.signin_required && (
              <Button ref={reviewOpener} type="button" variant="link" size="sm" className={quietAct} disabled={locked} onClick={() => setSheetOpen(true)}>
                Review recording
              </Button>
            )}
            <Button
              ref={withdrawOpener}
              type="button"
              variant="link"
              size="sm"
              className={`${quietAct} text-destructive`}
              disabled={locked}
              aria-expanded={withdrawing}
              aria-controls={withdrawing ? `recording-link-withdraw-${sessionId}` : undefined}
              onClick={() => (withdrawing ? closeWithdraw() : setWithdrawing(true))}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {status === "published" && link && (
        <div className="grid justify-items-start gap-1">
          <p className="flex flex-wrap items-center gap-x-1.5 text-ui text-foreground">
            {review.isSuccess ? (
              <a
                href={review.data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-1 underline underline-offset-4"
              >
                {shownLabel}
                <ExternalLink aria-hidden className="size-4" strokeWidth={1.75} />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            ) : (
              <span>{shownLabel}</span>
            )}
            <span>· Published</span>
          </p>
          {audience && <p className="text-meta text-muted-foreground">{audience}</p>}
          <div className="flex flex-wrap items-center gap-3">
            <Button
              ref={withdrawOpener}
              type="button"
              variant="link"
              size="sm"
              className={`${quietAct} text-destructive`}
              disabled={locked}
              aria-expanded={withdrawing}
              aria-controls={withdrawing ? `recording-link-withdraw-${sessionId}` : undefined}
              onClick={() => (withdrawing ? closeWithdraw() : setWithdrawing(true))}
            >
              Withdraw
            </Button>
          </div>
        </div>
      )}

      {withdrawing && live && (
        <div id={`recording-link-withdraw-${sessionId}`} role="group" aria-label={status === "published" ? "Withdraw from students" : "Remove the link"} className="grid w-full min-w-0 gap-3 rounded-control bg-well p-3">
          <p className="text-ui text-foreground">
            {status === "published"
              ? "Withdrawing removes it from the session, and students see that it was withdrawn. The recording stays in Zoom. Add a corrected link afterwards."
              : "Removing takes the link off the session. Students never saw it, and the recording stays in Zoom. Add the right link afterwards."}
          </p>
          <div className="grid min-w-0 gap-1.5">
            <Label htmlFor={`recording-link-withdraw-reason-${sessionId}`}>{status === "published" ? "Why are you withdrawing it?" : "Why are you removing it?"}</Label>
            <Textarea
              id={`recording-link-withdraw-reason-${sessionId}`}
              rows={3}
              maxLength={500}
              required
              autoFocus
              value={withdrawReason}
              onChange={(event) => setWithdrawReason(event.target.value)}
            />
            <p className="text-meta text-muted-foreground">Kept with the recording&apos;s history. Students do not see it.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive-surface hover:text-destructive"
              disabled={locked || !withdrawReason.trim()}
              onClick={() => void transition("withdraw", withdrawReason.trim())}
            >
              {busy === "withdraw" && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
              {status === "published" ? "Withdraw from students" : "Remove link"}
            </Button>
            <Button type="button" size="sm" variant="outline" disabled={busy !== null} onClick={closeWithdraw}>Cancel</Button>
          </div>
        </div>
      )}

      {receipt && <p role="status" aria-live="polite" className="text-ui text-foreground">{receipt}</p>}
      {message && <p role="status" aria-live="polite" className="text-ui text-muted-foreground">{message}</p>}
      {readbackBlocked && (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => void refreshProjection().catch(() => setMessage("The recording still could not be reloaded. Nothing can be changed until it is."))}>
            Reload the recording
          </Button>
        </div>
      )}

      {link?.placement_id && (
        <div className="flex flex-wrap items-center gap-3">
          <Button type="button" variant="link" size="sm" className={quietAct} disabled={busy !== null} onClick={() => void showHistory()}>History</Button>
        </div>
      )}
      {history && (
        <section aria-labelledby={`recording-link-history-${sessionId}`} className="grid gap-1">
          <h4 id={`recording-link-history-${sessionId}`} className="text-ui font-medium text-foreground">History</h4>
          <ol className="grid gap-1 text-ui text-muted-foreground">
            {history.map((event) => (
              <li key={event.event_no}>
                {event.action === "withdraw" && event.prior_status === "withheld" ? "Removed" : EVENT_WORD[event.action]} {when(event.created_at)}
                {event.reason && <span className="block text-foreground">Why: {event.reason}</span>}
              </li>
            ))}
          </ol>
        </section>
      )}

      {sheetOpen && status === "withheld" && link && (
        <Sheet open onOpenChange={(open) => { if (!open) closeSheet(); }}>
          <SheetContent
            hideDefaultTitle
            className="w-full sm:w-[36rem]"
            onCloseAutoFocus={(event) => { event.preventDefault(); reviewOpener.current?.focus(); }}
          >
            <SheetHeader>
              <SheetTitle>Publish recording</SheetTitle>
              <SheetDescription>{sessionWord === "this session" ? "This session" : sessionWord} · {providerWord} · Not published</SheetDescription>
            </SheetHeader>
            <SheetBody className="grid content-start gap-4">
              <section aria-labelledby={`recording-link-before-${sessionId}`} className="grid gap-1">
                <h3 id={`recording-link-before-${sessionId}`} className="text-ui font-semibold">Before it publishes</h3>
                <ul className="grid gap-1 text-ui text-foreground">
                  <li>No passcode or token in the link. Checked by IMAM</li>
                  <li>{audience ?? "Viewers sign in to view"}. Set in {school}&apos;s Zoom account</li>
                </ul>
              </section>

              <div className="grid justify-items-start gap-1">
                {review.isPending && (
                  <p className="flex min-h-11 items-center gap-2 text-ui text-muted-foreground">
                    <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />
                    Finding the saved link
                  </p>
                )}
                {(review.isError || (review.isSuccess && !review.data)) && (
                  <p role="status" className="flex min-h-11 flex-wrap items-center gap-x-2 text-ui text-muted-foreground">
                    The saved link could not be read.
                    <Button type="button" variant="link" size="sm" className={quietAct} onClick={() => void review.refetch()}>Try again</Button>
                  </p>
                )}
                {review.isSuccess && review.data && (
                  <p className="flex flex-wrap items-center gap-x-2 text-ui">
                    <Button asChild variant="link" size="sm" className={`${quietAct} has-[>svg]:px-0`}>
                      <a href={review.data.url} target="_blank" rel="noopener noreferrer">
                        Open the recording
                        <ExternalLink aria-hidden className="size-4" strokeWidth={1.75} />
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    </Button>
                    <span className="text-meta text-muted-foreground">opens in a new tab</span>
                  </p>
                )}
              </div>

              <div className="grid justify-items-start gap-1">
                <label htmlFor={`recording-link-notice-${sessionId}`} className="flex cursor-pointer items-start gap-2 text-ui text-foreground">
                  <RecordSelectionCheckbox
                    id={`recording-link-notice-${sessionId}`}
                    className="-my-3"
                    checked={noticeConfirmed}
                    disabled={locked}
                    aria-describedby={noticeMeta ? `recording-link-notice-meta-${sessionId}` : undefined}
                    onCheckedChange={(checked: boolean | "indeterminate") => {
                      if (checked !== true || noticeConfirmed) return;
                      void recordNotice();
                    }}
                  />
                  <span>Learners were told this session is recorded</span>
                </label>
                {noticeMeta && <p id={`recording-link-notice-meta-${sessionId}`} className="text-meta text-muted-foreground">{noticeMeta}</p>}
              </div>

              <label htmlFor={`recording-link-attest-${sessionId}`} className="flex cursor-pointer items-start gap-2 text-ui text-foreground">
                <RecordSelectionCheckbox
                  id={`recording-link-attest-${sessionId}`}
                  className="-my-3"
                  checked={attested}
                  disabled={!review.isSuccess || locked}
                  onCheckedChange={(checked: boolean | "indeterminate") => setAttested(checked === true)}
                />
                <span>I opened the link. It plays this session&apos;s recording and shows no chat.</span>
              </label>

              <div className="grid gap-1">
                <p className="text-ui text-foreground">
                  Students will see it as <strong className="font-semibold">{label.trim() || defaultName}</strong>
                  {!renaming && (
                    <>
                      {" · "}
                      <Button type="button" variant="link" size="sm" className={quietAct} disabled={locked} onClick={() => setRenaming(true)}>rename</Button>
                    </>
                  )}
                </p>
                {renaming && (
                  <Input
                    aria-label="Name students see"
                    className="max-w-md"
                    value={label}
                    autoFocus
                    onChange={(event) => setLabel(event.target.value)}
                  />
                )}
              </div>

              <ul className="grid gap-1 text-meta text-muted-foreground">
                <li>Students enrolled on this course can open it from this session</li>
                <li>Withdrawing it later removes it from the session; the recording stays in Zoom</li>
              </ul>

              {sheetError && <p role="alert" className="text-ui text-destructive">{sheetError}</p>}
            </SheetBody>
            <SheetFooter>
              <Button type="button" disabled={!canPublish} onClick={() => void transition("publish")}>
                {busy === "publish" && <Loader2 aria-hidden className="size-4 animate-spin" strokeWidth={1.75} />}
                Publish recording
              </Button>
              <Button type="button" variant="outline" disabled={busy !== null} onClick={closeSheet}>Keep it private</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      )}
    </section>
  );
}
